import { useState } from 'react';
import { FiMessageSquare, FiChevronLeft } from 'react-icons/fi';
import { ChatSidebar } from './chat-sidebar';
import { ChatPanel } from './chat-panel';
import type { AssistantScope } from '@/modules/assistant';
import { useSelectAssistantThread, useAssistantRoute } from '@/modules/assistant';

export type ChatLayoutProps = {
  /** Which assistant family to ask — decided by the route. */
  scope: AssistantScope;
};

/**
 * The full-page chat: history sidebar on the left, conversation on the right.
 *
 * On narrow viewports the sidebar slides over the conversation and can be
 * toggled with the back button. On wide viewports both panels are always visible.
 */
export function ChatLayout({ scope }: ChatLayoutProps) {
  const route = useAssistantRoute();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const selectThread = useSelectAssistantThread();

  const isNarrow = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div className="flex h-dvh overflow-hidden bg-surface">
      {isNarrow && sidebarOpen ? (
        // Narrow: sidebar slides over the conversation.
        <>
          <div className="fixed inset-y-0 left-0 z-40 w-80 bg-surface border-r border-border flex flex-col">
            <div className="flex items-center justify-between gap-2 border-b border-border bg-surface/50 px-3 py-2">
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                className="h-8 w-8 shrink-0 p-0"
                aria-label="Close conversations"
              >
                <FiChevronLeft className="h-4 w-4" aria-hidden="true" />
              </button>
              <h2 className="font-semibold text-content">Conversations</h2>
            </div>
            <ChatSidebar
              onSelect={(id) => {
                selectThread(id, scope);
                setSidebarOpen(false);
              }}
              onNewConversation={() => {
                selectThread(null, scope);
                setSidebarOpen(false);
              }}
            />
          </div>
          <div className="pl-80">
            <ChatPanel route={route} scope={scope} />
          </div>
        </>
      ) : (
        // Wide: both panels side by side.
        <div className="flex h-dvh overflow-hidden">
          <div className="w-80 border-r border-border bg-surface flex flex-col hidden md:flex">
            <ChatSidebar
              onSelect={(id) => selectThread(id, scope)}
              onNewConversation={() => selectThread(null, scope)}
            />
          </div>
          <div className="flex-1 min-w-0 flex flex-col">
            <button
              type="button"
              className="absolute left-3 top-3 z-20 h-8 w-8 shrink-0 p-0 rounded-control border border-border bg-surface text-content-muted hover:bg-surface-sunken md:hidden"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open conversations"
            >
              <FiMessageSquare className="h-4 w-4" aria-hidden="true" />
            </button>
            <ChatPanel route={route} scope={scope} />
          </div>
        </div>
      )}
    </div>
  );
}
